import { useEffect, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import styled from '@emotion/styled'
import { OrderType, PrepType, ServiceType } from '@open-tender/types'
import {
  fetchLocation,
  resetOrderType,
  selectBrand,
  selectCartTotal,
  selectMenuSlug,
  selectOrder,
  setOrderServiceType,
  setPrepType,
  setRequestedAt,
  setTable,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { NavMenu } from 'components/buttons'
import {
  Body,
  Button,
  Content,
  Header,
  Main,
  PageContainer,
  PageContent,
  Loading,
  Text,
  HeaderLogoWithName,
} from 'components'

const QRView = styled.div``

const QRHeader = styled.div`
  text-align: center;

  h1 {
    @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
      font-size: ${(props) => props.theme.fonts.sizesMobile.xBig};
    }
  }

  h1 span {
    display: block;
  }

  p {
    margin: 1rem 0 0;
  }
`

const QRButtons = styled.div`
  label: QRButtons;
  margin: 3rem 0 0;
  display: flex;
  justify-content: center;
  align-items: center;

  button + button {
    margin: 0 0 0 1rem;
  }
`

const notFoundMsg =
  "Sorry, but we couldn't find this location. Please try again"

const validPrepTypes = ['EAT_HERE', 'TAKE_OUT']

const makePrepType = (prepType: PrepType | null) => {
  if (!prepType) return null
  const upper = prepType.toUpperCase()
  return validPrepTypes.includes(upper) ? upper : null
}

const QR = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [init, setInit] = useState(false)
  const { id: locationId } = useParams()
  const { title: siteTitle } = useAppSelector(selectBrand) || {}
  const menuSlug = useAppSelector(selectMenuSlug)
  const order = useAppSelector(selectOrder)
  const cartTotal = useAppSelector(selectCartTotal).toFixed(2)
  const { prepType, revenueCenter, loading, error } = order
  const { revenue_center_type: orderType, name } = revenueCenter || {}
  const isLoading = loading === 'pending'
  const errMsg =
    error && typeof error !== 'string' && error.detail.includes('exist')
      ? notFoundMsg
      : error
  const query = new URLSearchParams(useLocation().search)
  const serviceType = query.get('service_type') || 'WALKIN'
  const table = query.get('table') || null
  const prep_type = query.get('prep_type') || null
  const presetPrepType = makePrepType(prep_type as PrepType)
  const revenueCenterId = parseInt(locationId ?? '0')

  useEffect(() => {
    dispatch(resetOrderType())
    dispatch(setPrepType(presetPrepType))
    dispatch(fetchLocation({ revenueCenterId, cartTotal }))
    setInit(true)
  }, [dispatch, presetPrepType, locationId, revenueCenterId, cartTotal])

  useEffect(() => {
    if (init && menuSlug !== '/' && prepType) {
      dispatch(setTable(table))
      dispatch(setRequestedAt('asap'))
      dispatch(
        setOrderServiceType({
          orderType: (orderType ?? 'OLO') as OrderType,
          serviceType: (serviceType ?? 'WALKIN') as ServiceType,
          isOutpost: false,
        })
      )
      navigate(menuSlug)
    }
  }, [
    init,
    prepType,
    menuSlug,
    orderType,
    serviceType,
    table,
    dispatch,
    navigate,
  ])

  return (
    <>
      <Helmet>
        <title>Dine In | {siteTitle}</title>
      </Helmet>
      <Content>
        <Header left={<HeaderLogoWithName />} right={<NavMenu />} />
        <Main>
          <PageContainer style={{ maxWidth: '76.8rem' }}>
            <PageContent>
              {isLoading ? (
                <Loading text="Loading menu..." />
              ) : errMsg ? (
                <Text color="error" style={{ width: '100%' }}>
                  {errMsg as string}
                </Text>
              ) : (
                <QRView>
                  <QRHeader>
                    {name ? (
                      <h1>
                        <span>Welcome to</span>
                        <span>{name}</span>
                      </h1>
                    ) : (
                      <h1>Welcome</h1>
                    )}
                    <Body as="p" size="main">
                      Please choose an order type
                    </Body>
                  </QRHeader>
                  <QRButtons>
                    <Button
                      onClick={() => dispatch(setPrepType('EAT_HERE'))}
                      size="default"
                    >
                      Eat Here
                    </Button>
                    <Button
                      onClick={() => dispatch(setPrepType('TAKE_OUT'))}
                      size="default"
                    >
                      Take Out
                    </Button>
                  </QRButtons>
                </QRView>
              )}
            </PageContent>
          </PageContainer>
        </Main>
      </Content>
    </>
  )
}

export default QR
